import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
const SettingsContext = createContext({
    companyId: null,
    isLoading: false,
    refreshAllSettings: () => { },
    registerRefreshFunction: () => { }
});
export const SettingsProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    // Reference to child contexts' refresh functions
    const refreshFunctionsRef = useRef({});
    // Extract companyId from URL
    useEffect(() => {
        const path = window.location.pathname.split("/");
        const urlCompanyId = path[1];
        // Only update if it's a valid companyId (not an empty string or special route)
        if (urlCompanyId && !['page-login', 'page-register', 'page-error'].includes(urlCompanyId)) {
            // Only update if this is a different companyId
            if (urlCompanyId !== companyId) {
                setCompanyId(urlCompanyId);
            }
        }
    }, [window.location.pathname]);
    // Note: Each child context fetches its own data on companyId change.
    // refreshAllSettings() is available for manual refresh (e.g. after SIE import).

    // Register a refresh function from a child context
    // Memoized to prevent child contexts from re-running effects on every render
    const registerRefreshFunction = useCallback((name, fn) => {
        refreshFunctionsRef.current[name] = fn;
        return () => {
            delete refreshFunctionsRef.current[name];
        };
    }, []);

    // Refresh all settings or specific categories
    const refreshAllSettings = useCallback(async (categories = null) => {
        if (!companyId) {
            console.warn("Cannot refresh settings: No companyId available");
            return;
        }
        setIsLoading(true);
        try {
            const functionsToCall = categories
                ? categories.filter(c => refreshFunctionsRef.current[c]).map(c => refreshFunctionsRef.current[c])
                : Object.values(refreshFunctionsRef.current);
            await Promise.all(functionsToCall.map(fn => fn()));
        } catch (error) {
            console.error('Error refreshing settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    return (
        <SettingsContext.Provider value={{
            companyId,
            isLoading,
            refreshAllSettings,
            registerRefreshFunction
        }}>
            {children}
        </SettingsContext.Provider>
    );
};
export const useSettings = () => useContext(SettingsContext);
export default SettingsContext;
